import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Select, Input, DatePicker, Row, Col, Space, Typography, Spin, Form, Switch } from "antd";
import dayjs from "dayjs";

import { downloadPrescriptionExcel, fetchPrescriptionFilters, fetchPrescriptions } from "../../api/prescription";
import PrescriptionsData from "../../types/PrescriptionsData";
import { PrescriptionFilter } from "../../types/PrescriptionFilter";
import { statusArray, statusOptions } from "../../data/status";
import { phonePrefix } from "../../data/country";
import PrescriptionTable from "./PrescriptionTable";
import { downloadExcelData } from "../../data/util";
import PhoneInputV2 from "../PhoneInputV2";
import { parseDate } from "../../util/date";
import { FilterType, selectedFiltersInitialState, SelectedFiltersType } from "./types";

import "./styles.css";
import { operationNames, userHasAccess } from "../../data/pages";
import { useTexts } from "../../hooks/useTexts";

const { Title } = Typography;

const PrescriptionView = (): JSX.Element => {
    const { t } = useTexts();
    const [data, setData] = useState<PrescriptionsData>({
        prescriptions: [],
        pagination: {
            page: 0,
            pageSize: 50,
            pageCount: 0,
            elementCount: 0,
        }
    });

    const [filters, setFilters] = useState<FilterType>({
        userData: [],
        placeData: [],
        productData: [],
        statuses: []
    });

    const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>(selectedFiltersInitialState);
    const [loading, setLoading] = useState(false);

    const translatedStatusOptions = useMemo(() => {
        return statusOptions.map(status => ({
            id: status.id,
            name: t('status', status.key)
        }));
    }, [t]);

    const getParams = (filters: SelectedFiltersType) => {
        let params: any = {
            productId: filters?.productId || [],
            userId: filters?.userId || [],
            placeId: filters?.placeId || [],
            status: filters.statusId?.map(id => statusArray[id - 1]) || [],
            cDtFrom: parseDate(filters?.cDtFrom),
            cDtTo: parseDate(filters?.cDtTo),
            uDtFrom: parseDate(filters?.uDtFrom),
            uDtTo: parseDate(filters?.uDtTo),
            page: filters?.page ?? null,
            pageSize: filters?.pageSize ?? null,
            sortBy: filters?.sortBy ?? null,
            sortDir: filters?.sortDir ?? null,
            phoneNumber: !!filters?.phoneNumber ? encodeURIComponent(`${phonePrefix}${filters.phoneNumber}`) : '',
            mFirstName: filters?.mFirstName ?? null,
            mLastName: filters?.mLastName ?? null,
            flagged: filters?.flagged
        };
        return params;
    };

    const fetchData = async (filters: SelectedFiltersType) => {
        setLoading(true);
        const params = getParams(filters);
        fetchPrescriptions(params)
            .then(data => {
                if (!!data) {
                    setData(data);
                }
            })
            .catch(err => console.error("Error occurred: ", err))
            .finally(() => setLoading(false));
    };

    const fetchFilters = async () => {
        const response: PrescriptionFilter | void = await fetchPrescriptionFilters();
        if (response) {
            setFilters({ ...filters, ...response });
        }
    };

    const applyFilters = () => {
        const filtersWithResetPage = { ...selectedFilters, page: 0 };
        setSelectedFilters(filtersWithResetPage);
        fetchData(filtersWithResetPage);
    };

    const clearFilter = () => {
        setSelectedFilters(selectedFiltersInitialState);
        fetchData(selectedFiltersInitialState);
    };

    const downloadExcel = async (filters: SelectedFiltersType) => {
        const params = getParams(filters);
        const data = await downloadPrescriptionExcel(params);
        downloadExcelData(data, "Gonderisler");
    };

    useEffect(() => {
        fetchData(selectedFilters);
        fetchFilters();
    }, []);

    useEffect(() => {
        const filtersWithResetPage = { ...selectedFilters, page: 0 };
        setSelectedFilters(filtersWithResetPage);
        fetchData(filtersWithResetPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilters.sortBy, selectedFilters.sortDir]);

    return (
        <div style={{ marginTop: '24px' }}>
            <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
                <Form layout="vertical" className="responsive-form">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'selectProduct')}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder={t('prescription', 'selectProduct')}
                                    value={selectedFilters.productId}
                                    onChange={val => setSelectedFilters({ ...selectedFilters, productId: val })}
                                    options={filters?.productData?.map(item => ({ label: item.name, value: item.id }))}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'selectPlace')}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder={t('prescription', 'selectPlace')}
                                    value={selectedFilters.placeId}
                                    onChange={val => setSelectedFilters({ ...selectedFilters, placeId: val })}
                                    options={filters?.placeData?.map(item => ({ label: item.name, value: item.id }))}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'selectUser')}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder={t('prescription', 'selectUser')}
                                    value={selectedFilters.userId}
                                    onChange={val => setSelectedFilters({ ...selectedFilters, userId: val })}
                                    options={filters?.userData?.map(item => ({ label: item.name, value: item.id }))}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'selectStatus')}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    showSearch
                                    placeholder={t('prescription', 'selectStatus')}
                                    value={selectedFilters.statusId}
                                    onChange={val => setSelectedFilters({ ...selectedFilters, statusId: val })}
                                    options={translatedStatusOptions.map(item => ({ label: item.name, value: item.id }))}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'createdDateFrom')}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedFilters.cDtFrom}
                                    onChange={date => setSelectedFilters({ ...selectedFilters, cDtFrom: date })}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'createdDateTo')}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedFilters.cDtTo}
                                    onChange={date => setSelectedFilters({ ...selectedFilters, cDtTo: date })}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'updatedDateFrom')}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedFilters.uDtFrom}
                                    onChange={date => setSelectedFilters({ ...selectedFilters, uDtFrom: date })}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'updatedDateTo')}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    value={selectedFilters.uDtTo}
                                    onChange={date => setSelectedFilters({ ...selectedFilters, uDtTo: date })}
                                    format="DD/MM/YYYY"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'mediatorPhone')}>
                                <PhoneInputV2
                                    placeholder={t('prescription', 'mediatorPhone')}
                                    value={selectedFilters.phoneNumber}
                                    onChange={val => setSelectedFilters({ ...selectedFilters, phoneNumber: val })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'mediatorFirstname')}>
                                <Input
                                    value={selectedFilters.mFirstName}
                                    onChange={e => setSelectedFilters({ ...selectedFilters, mFirstName: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item label={t('prescription', 'mediatorLastname')}>
                                <Input
                                    value={selectedFilters.mLastName}
                                    onChange={e => setSelectedFilters({ ...selectedFilters, mLastName: e.target.value })}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Form.Item label={t('common', 'sortBy')}>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Select
                                            style={{ width: '60%' }}
                                            value={selectedFilters.sortBy}
                                            onChange={val => setSelectedFilters({ ...selectedFilters, sortBy: val })}
                                            options={[
                                                { label: t('prescription', 'sortByUpdatedDate'), value: 'updated_at' },
                                                { label: t('prescription', 'sortByCreatedDate'), value: 'created_at' },
                                                { label: t('prescription', 'sortByProduct'), value: 'product_name' },
                                                { label: t('prescription', 'sortByPlace'), value: 'place_name' },
                                            ]}
                                        />
                                        <Select
                                            style={{ width: '40%' }}
                                            value={selectedFilters.sortDir}
                                            onChange={val => setSelectedFilters({ ...selectedFilters, sortDir: val })}
                                            options={[
                                                { label: t('common', 'ascending'), value: 'ASC' },
                                                { label: t('common', 'descending'), value: 'DESC' },
                                            ]}
                                        />
                                    </Space.Compact>
                                </Form.Item>
                            </Space>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} justify="space-between" align="middle" style={{ marginTop: '16px' }}>
                        <Col xs={24} md={12}>
                            {userHasAccess(operationNames.VIEW_FLAGGED_PRESCRIPTIONS) && (
                                <Space>
                                    <Switch
                                        checked={selectedFilters.flagged}
                                        onChange={checked => setSelectedFilters({ ...selectedFilters, flagged: checked })}
                                    />
                                    <Typography.Text>{t('prescription', 'selected')}</Typography.Text>
                                </Space>
                            )}
                        </Col>
                        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                            <Space wrap style={{ justifyContent: 'flex-end' }}>
                                <Button onClick={() => downloadExcel({ ...selectedFilters, pageSize: 1000000 })}>
                                    {t('prescription', 'downloadExcel')}
                                </Button>
                                <Button type="primary" onClick={applyFilters} loading={loading}>
                                    {t('prescription', 'applyFilter')}
                                </Button>
                                <Button danger onClick={clearFilter}>
                                    {t('prescription', 'clearFilter')}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>

            <PrescriptionTable
                data={data}
                loading={loading}
                refresh={() => fetchData({ ...selectedFilters })}
                onPageChange={(page, pageSize) => {
                    const updatedFilters = { ...selectedFilters, page: page, pageSize };
                    setSelectedFilters(updatedFilters);
                    fetchData(updatedFilters);
                }}
            />
        </div>
    );
};

export default PrescriptionView;